import moment from 'moment-timezone';
import Request from 'files/Request.js';

class PaymentClass {

    amount = null;
    category = null;
    date = null;
    id = null;
    metadata = null;
    payment_method = null;
    payment_method_id = null;
    status = null;
    target = null;
    title = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {

        this.amount = props.amount;
        this.category = props.category;
        this.date = props.date && moment.utc(props.date).local();
        this.id = props.id;
        this.metadata = props.metadata;
        this.payment_method = props.payment_method;
        this.payment_method_id = props.payment_method_id;
        this.status = props.status;
        this.target = props.target;
        this.title = props.title;
        this.user_id = props.user_id;

        // determine if a metadata date needs to be prepared
        if(this.metadata?.invoice_date) {
            this.metadata.invoice_date = moment.utc(this.metadata.invoice_date).local();
        }
        return this;
    }
}

class MethodClass {

    active = null;
    brand = null;
    date = null;
    expiration_date = null;
    funding = null;
    id = null;
    is_default = false;
    last4 = null;
    name = null;
    provider = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.active = props.active;
        this.brand = props.brand; // Now an object with { code, text }
        this.date = props.date;
        this.expiration_date = props.expiration_date && moment(props.expiration_date);
        this.funding = props.funding; // Now an object with { code, text }
        this.id = props.id;
        this.is_default = props.is_default || false;
        this.last4 = props.last4;
        this.name = props.name;
        this.provider = props.provider;
        this.user_id = props.user_id;
        return this;
    }

    getExpiration = () => {
        if(!this.expiration_date) return 'No expiration';
        return `Expires ${this.expiration_date.format('MM/YY')}`;
    }

    getIcon = () => {
        // Use the brand.code or brand.text to determine icon
        let brandName = this.brand?.text || this.brand?.code || '';
        switch(brandName.toLowerCase()) {
            case 'american express':
            case 'amex':
            return 'fab fa-cc-amex';

            case 'discover':
            return 'fab fa-cc-discover';

            case 'mastercard':
            return 'fab fa-cc-mastercard';

            case 'visa':
            return 'fab fa-cc-visa';

            default:
            return 'far fa-credit-card';
        }
    }
}

class RefundClass {

    amount = null;
    date = null;
    external_id = null;
    id = null;
    payment_id = null;
    status = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.amount = props.amount;
        this.date = props.date && moment(props.date);
        this.external_id = props.external_id;
        this.id = props.id;
        this.payment_id = props.payment_id;
        this.refund_id = props.refund_id;
        this.status = props.status;
        this.user_id = props.user_id;
        return this;
    }
}

class SubscriptionClass {

    active = null;
    amount = null;
    category = null;
    costs = null;
    created = null;
    execution_date = null;
    id = null;
    payment_method = null;
    payment_method_id = null;
    schedule = null;
    tier = null;
    user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.active = props.active;
        this.amount = props.amount;
        this.category = props.category;
        this.costs = props.costs;
        this.created = props.created && moment.utc(props.created);
        this.execution_date = props.execution_date && moment.utc(props.execution_date);
        this.id = props.id;
        this.payment_method = props.payment_method;
        this.payment_method_id = props.payment_method_id;
        this.schedule = props.schedule;
        this.tier = props.tier;
        this.user_id = props.user_id;
        return this;
    }
}

class TransactionClass {

    amount = null;
    date = null;
    external_id = null;
    id = null;
    payment_id = null;
    payment_method_id = null;
    processed_by_user_id = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.amount = props.amount;
        this.date = props.date && moment.utc(props.date);
        this.external_id = props.external_id;
        this.id = props.id;
        this.payment_id = props.payment_id;
        this.payment_method_id = props.payment_method_id;
        this.processed_by_user_id = props.processed_by_user_id;
        return this;
    }
}

const fetchPayment = async (utils, id) => {
    return new Promise(async (resolve, reject) => {
        try {
            let { payment } = await Request.get(utils, '/payments/', {
                id: id,
                type: 'details'
            });
            resolve(new PaymentClass().create(payment));

        } catch(e){
            reject(e);
        }
    });
}

const fetchPaymentMethod = async (utils, id, props) => {
    return new Promise(async (resolve, reject) => {
        try {
            let { method } = await Request.get(utils, '/payments/', {
                id: id,
                type: 'payment_method_details',
                ...props
            });
            resolve(new MethodClass().create(method));

        } catch(e){
            reject(e);
        }
    });
}

const fetchSubscription = async (utils, id) => {
    return new Promise(async (resolve, reject) => {
        try {
            let { subscription } = await Request.get(utils, '/payments/', {
                id: id,
                type: 'subscription_details'
            });
            resolve(new SubscriptionClass().create(subscription));

        } catch(e){
            reject(e);
        }
    });
}

const paymentStatus = {
    paid: 1,
    refunded: 2,
    unpaid: 3,
    failed: 4,
    partial_paid: 5,
    cancelled: 6
}

const paymentStatusToText = code => {
    switch(code) {
        case paymentStatus.paid:
        return 'Paid';

        case paymentStatus.refunded:
        return 'Refunded';
        
        case paymentStatus.unpaid:
        return 'Unpaid';
        
        case paymentStatus.failed:
        return 'Failed';
        
        case paymentStatus.partial_paid:
        return 'Partial Payment';
        
        case paymentStatus.cancelled:
        return 'Cancelled';
        
        default:
        return 'Unknown status';
    }
}

const categories = {
    subscription_premium: 1,
    ala_carte_accounts: 2,
    credits: 3
}

const categoryToText = code => {
    switch(code) {
        case categories.subscription_premium:
        return 'Subscription Premium';

        case categories.ala_carte_accounts:
        return 'Locket Passwords Add On';

        case categories.credits:
        return 'Credits';
    }
}

const promotionCategories = {
    fixed: 1,
    percentage: 2,
    replace: 3
}

const promotionCategoryToText = code => {
    switch(code) {
        case promotionCategories.fixed:
        return 'Fixed Amount';

        case promotionCategories.percentage:
        return 'Percentage';

        case promotionCategories.replace:
        return 'Price Replacement';

        default:
        return 'Unknown promotion type';
    }
}

const schedules = {
    daily: 1,
    weekly: 2,
    bi_weekly: 3,
    monthly: 4,
    quarterly: 5,
    annually: 6
}

const schedleToText = code => {
    switch(code) {
        case schedules.daily:
        return 'Daily';

        case schedules.weekly:
        return 'Weekly';

        case schedules.bi_weekly:
        return 'Bi-Weekly';

        case schedules.monthly:
        return 'Monthly';

        case schedules.quarterly:
        return 'Quarterly';

        case schedules.annually:
        return 'Annually';

        default:
        return 'Unknown schedule type';
    }
} 

const subscriptionCategories = {
    bronze: 1,
    silver: 2,
    gold: 3,
    starter: 4
}

export default {
    categories: {
        get: () => categories,
        toText: categoryToText
    },
    create: props => new PaymentClass().create(props),
    get: fetchPayment,
    new: () => new PaymentClass(),
    Method: {
        create: props => new MethodClass().create(props),
        get: fetchPaymentMethod,
        new: () => new MethodClass()
    },
    Promotion: {
        categories: {
            get: () => promotionCategories,
            toText: promotionCategoryToText
        }
    },
    Refund: {
        create: props => new RefundClass().create(props),
    },
    status: {
        get: () => paymentStatus,
        toText: paymentStatusToText
    },
    Subscription: {
        categories: {
            get: () => subscriptionCategories
        },
        create: props => new SubscriptionClass().create(props),
        get: fetchSubscription,
        new: () => new SubscriptionClass(),
        schedules: {
            get: () => schedules,
            toText: schedleToText
        }
    },
    Transaction: {
        create: props => new TransactionClass().create(props),
    },
}
